/**
PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 *  Copyright © 2025 Pearson Education, Inc.
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
* */

/**
 * TabularStyles
 *
 * @file TabularStyles.jsx
 * @author Mohamed yasar arabath M
 * @since 1.0.0
 */

import { makeStyles } from '@material-ui/core';
import mixins from '../../../assets/styles/mixins.scss';
import CommonStyles from '../../../assets/styles/CommonStyles';

const {
  subtitle2
} = CommonStyles();

const TabularStyles = makeStyles({
  paddingCheckbox: {
    padding: '0px',
    color: `${mixins.amethyst} !important`,
    '& svg': {
      width: 20,
      height: 20
    },
    '&.Mui-disabled': {
      '& svg path': {
        fill: '#E6E6E6',
        stroke: '#CCCCCC',
        strokeWidth: 2
      },

      '& svg rect': {
        fill: '#E6E6E6',
        stroke: '#CCCCCC',
        strokeWidth: 2
      }
    }
  },
  root: {
  },
  tablecontainer: {
    maxHeight: 'inherit'
  },
  tableWrapper: {
    maxHeight: 'var(--table-container-max-height)',
    overflow: 'hidden'
  },
  table: {
    '--table-head-padding-top': 'var(--table-container-head-padding-top, 0px)',
    '--table-head-padding-left': 'var(--table-container-head-padding-left, 0px)',
    '--table-head-padding-right': 'var(--table-container-head-padding-right, 0px)',
    '--table-head-padding-bottom': 'var(--table-container-head-padding-bottom, 12px)',
    '--table-cell-padding-top': 'var(--table-container-cell-padding-top, 8px)',
    '--table-cell-padding-bottom': 'var(--table-container-cell-padding-bottom, 8px)',
    '--table-cell-padding-left': 'var(--table-container-cell-padding-left, 0px)',
    '--table-cell-padding-right': 'var(--table-container-cell-padding-right, 0px)',
    '--table-cell-gap': 'var(--table-container-cell-gap, 12px)'
  },
  tableBodyRowRoot: {
    '&:last-child': {
      '& $tableCell': {
        borderBottom: 'none'
      }
    },
    '&:hover': {
      '& .editActionIcon': {
        visibility: 'visible'
      }
    },
    '&.errorRow': {
      backgroundColor: mixins.accentLightRed,
      '& $tableCell': {
        borderBottom: '1px solid rgba(13, 0, 77, 0.15)'
      },
      '&:hover': {
        backgroundColor: mixins.accentLightRed
      }
    }
  },
  tableRowRoot: {
    '& th': {
      backgroundColor: 'unset',
      fontFeatureSettings: '\'liga\' off',
      fontFamily: mixins.fontFamilyPlusSans,
      fontSize: mixins.fontSize12,
      fontStyle: 'normal',
      fontWeight: mixins.fontWeight500,
      lineHeight: '166.667%',
      padding: '0px',
      paddingBottom: 'var(--table-head-padding-bottom)',
      paddingTop: 'var(--table-head-padding-top)',
      paddingLeft: 'var(--table-head-padding-left)',
      paddingRight: 'var(--table-head-padding-right)',
      color: mixins.greyScaleColor
    }
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    width: '100%'
  },
  titleContainerLeft: {
    flexShrink: 0
  },
  titleContainerRight: {
    flex: 1,
    marginBottom: 20
  },
  primary: {
    display: 'flex',
    alignItems: 'center'
  },
  primaryGap: {
    gap: 8
  },
  primaryCol1: {
    display: 'flex',
    padding: '0 8px',
    alignItems: 'flex-start',
    flex: '1 0 0',
    flexDirection: 'column'
  },
  subtitle2,
  caption: {
    color: mixins.zambezi,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize12,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight400,
    lineHeight: '166.667%'
  },
  body2: {
    color: mixins.navyColor,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight500,
    lineHeight: '157.143%'
  },
  amethystColor: {
    color: mixins.amethystColor,
    marginLeft: 8
  },
  textPrimaryColor: {
    color: mixins.color_text_primary
  },
  attemptedBar: {
    height: 6,
    borderRadius: 4,
    width: 164,
    backgroundColor: mixins.amethystLightColor
  },
  correctBar: {
    height: 8,
    borderRadius: 4,
    backgroundColor: '#AE77FC'
  },
  chipLabel: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize12,
    fontWeight: mixins.fontWeight500,
    lineHeight: '20px',
    paddingLeft: '6px !important',
    paddingRight: '6px !important'
  },
  chipMathText: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize12,
    fontWeight: mixins.fontWeight500,
    lineHeight: '20px'
  },
  chip: {
    margin: '0 0px 2px 0px',
    fontWeight: 500,
    backgroundColor: mixins.feedBackColor,
    color: mixins.accentBrown,
    borderRadius: 4
  },
  checkIcon: {
    color: '#37B866',
    verticalAlign: 'middle',
    marginRight: 4
  },
  errorIcon: {
    color: '#FFAB00',
    verticalAlign: 'middle',
    marginRight: 4
  },
  linkButton: {
    '&&': {
      color: mixins.color_text_primary,
      textDecoration: 'none',
      fontFamily: mixins.fontFamilyPlusSans,
      fontSize: mixins.fontSize16,
      fontStyle: 'normal',
      fontWeight: mixins.fontWeight600,
      lineHeight: '24px'
    },
    '&&:hover': {
      color: mixins.linkDefault,
      textDecoration: 'underline'
    }
  },
  tableCell: {
    paddingTop: 'var(--table-cell-padding-top)',
    paddingBottom: 'var(--table-cell-padding-bottom)',
    paddingLeft: 'var(--table-cell-padding-left)',
    paddingRight: 'var(--table-cell-padding-right)',
    borderBottom: '1px solid rgba(13, 0, 77, 0.15)',
    position: 'relative',

    '&::after': {
      content: '""',
      position: 'absolute',
      top: 'var(--table-cell-padding-top)',
      right: 0,
      width: '1px',
      height: 'calc(100% - var(--table-cell-padding-top) - var(--table-cell-padding-bottom))',
      backgroundColor: 'rgba(13, 0, 77, 0.15)'
    },
    '&:first-child': {
      '& > $tableCellContent': {
        paddingLeft: 0
      }
    },
    '& > $tableCellContent': {
      paddingLeft: 'var(--table-cell-gap)',
      paddingRight: 'var(--table-cell-gap)'
    },

    '&:last-child': {
      '& > $tableCellContent': {
        paddingRight: 0
      },
      '&::after': {
        content: 'none'
      }
    }
  },
  tableThCell: {
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 'var(--table-head-padding-top)',
      right: 0,
      width: '1px',
      height: 'calc(100% - var(--table-head-padding-top) - var(--table-head-padding-bottom))',
      backgroundColor: 'rgba(13, 0, 77, 0.15)'
    }
  },
  tableCellContent: {
    display: 'flex',
    alignItems: 'flex-start'
  },
  sortIcon: {
    padding: 4,
    boxSizing: 'border-box',
    color: mixins.color_text_primary,
    marginLeft: 8,

    '& svg': {
      width: 16,
      height: 16
    }
  },
  imageContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 8
  },
  image: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    ...subtitle2,

    '&:has(span)': {
      borderRadius: '50%',
      border: `2px solid ${mixins.navyColor}`,
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 12
    },
    '& svg': {
      height: '100%',
      width: '100%',
      color: mixins.navyColor
    }
  },
  stickyHeader: {
    backgroundColor: '#fff !important',
    position: 'sticky'
  },
  dotIcon: {
    width: 6,
    height: 6
  },
  badge: {
    display: 'flex',
    padding: '0px 2px',
    borderRadius: 4,
    width: 'fit-content',
    gap: 4,
    maxHeight: 20,
    cursor: 'inherit'
  },
  badgeLabel: {
    padding: '0px 4px',
    textAlign: 'center',
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: '500',
    lineHeight: '20px'
  },
  dotIconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 16,
    height: 16
  },
  badge2: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    height: '20px',
    padding: '0 4px 2px 2px',
    borderRadius: '4px',
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: 500,
    fontSize: '12px',
    lineHeight: '16px',
    textAlign: 'center',
    verticalAlign: 'middle'
  },
  bullet: {
    display: 'inline-block',
    width: '16px',
    height: '16px',
    lineHeight: '16px',
    textAlign: 'center',
    fontWeight: 'bold'
  },
  text: {
    fontFamily: 'Plus Jakarta Sans',
    fontSize: 12,
    fontWeight: 500,
    lineHeight: mixins.lineHeight20,
    padding: '0px 2px 0px 0px'
  },

  completedOnText: {
    color: '#006632',
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight400,
    fontStyle: 'regular',
    fontSize: '12px',
    lineHeight: '20px'
  },
  pastDue: {
    color: '#045CAF',
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight400,
    fontStyle: 'regular',
    fontSize: '12px',
    lineHeight: '20px'
  },
  cursor: {
    cursor: 'default'
  },
  tableBorder: {
    position: 'unset',

    '&::after': {
      content: 'none'
    }
  },
  extraPadding: {
    padding: '0px 4px 0px 6px'
  },
  infoHeaderIcon: {
    marginLeft: 8
  },
  infoIcon: {
    width: 18,
    height: 18,
    color: mixins.color_text_primary,
    cursor: 'pointer'
  },
  infoTooltip: {
    minWidth: 320
  },
  infoIconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  questionNumberWrapper: {
    display: 'flex'
  },
  operator: {
    color: mixins.color_text_primary,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight600,
    lineHeight: '24px',
    margin: '0px 4px'
  },
  filterConatiner: {
    display: 'flex',
    gap: 8,
    flexShrink: 0,
    marginBottom: 12,
    alignItems: 'center'
  },

  filterLabel: {
    color: mixins.greyScaleColor,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight500,
    lineHeight: '18px',
    display: 'flex',
    alignItems: 'center'
  },

  filterSelect: {
    borderRadius: 4,
    backgroundColor: mixins.chalkWhiteFont,
    border: `1px solid ${mixins.amethyst}`,
    cursor: 'pointer',
    padding: 12,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,

    outline: 'none',
    appearance: 'none',
    WebkitAppearance: 'none',
    '&:hover': {
      backgroundColor: mixins.chalkWhiteFont,
      border: '1px solid #8E84DC',
      boxShadow: `0px 0px 0px 4px ${mixins.amethystLightColor}`
    },

    '&:focus': {
      backgroundColor: mixins.chalkWhiteFont,
      border: `1px solid ${mixins.primaryColor}`
    },
    '&:active': {
      backgroundColor: mixins.chalkWhiteFont,
      border: `1px solid ${mixins.primaryColor}`,
      boxShadow: 'none',
      transform: 'none'
    }
  },

  filterSelectContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%'
  },

  filterSelectedText: {
    color: mixins.timeIconColor,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: 600,
    lineHeight: mixins.lineHeight24,
    flex: '1 0 0',
    textTransform: 'none'
  },

  filterArrowIcon: {
    color: mixins.timeIconColor,
    fontSize: 24
  },

  filterPopover: {
    marginTop: 12,
    borderRadius: 4,
    minWidth: 220,
    background: mixins.chalkWhiteFont,
    border: `1px solid ${mixins.amethystColorV2}`
  },

  filterMenuItemContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    width: '100%'
  },
  filterMenuItem: {
    padding: 12,

    '&.MuiListItem-root': {
      padding: 12,
      transition: 'none !important'
    },

    '&.Mui-selected': {
      backgroundColor: '#E8E6F3'
    },

    '&.Mui-selected:hover': {
      backgroundColor: '#E8E6F3'
    },

    '&:hover': {
      backgroundColor: '#E8E6F3'
    }
  },
  filterMenuItemText: {
    color: mixins.timeIconColor,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: 400,
    lineHeight: mixins.lineHeight24
  },
  filterMenuItemSelectedText: {
    color: mixins.timeIconColor,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: 600,
    lineHeight: mixins.lineHeight24
  },
  filterCheckIcon: {
    width: 16,
    height: 15,
    color: mixins.timeIconColor
  },
  selectionCount: {
    color: mixins.greyScaleColor,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight700,
    lineHeight: '18px'
  },
  selectionLabel: {
    color: mixins.greyScaleColor,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight400,
    lineHeight: '18px'
  },
  iconButton: {
    color: mixins.navyColor,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight500,
    lineHeight: '22px',
    padding: 8,
    borderRadius: '1000px',
    minWidth: 'unset',
    textTransform: 'none',
    textDecoration: 'underline',

    '&:hover': {
      backgroundColor: 'transparent',
      color: mixins.linkHoverColor,
      textDecoration: 'underline'
    },
    '& > span': {
      minWidth: 'unset',
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0px 8px 2px 8px'
    },
    '&.Mui-disabled': {
      color: mixins.disabledTextColor,
      opacity: 'unset',
      pointerEvents: 'unset',

      '&:hover': {
        background: mixins.secondaryButtonHoverColor,
        color: 'inherit'
      }
    }
  },
  chipSelectContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 4
  },
  dropdownIconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    padding: 4,
    boxSizing: 'border-box'
  },
  select: {
    padding: '0px !important',
    minHeight: 'unset'
  },
  selectIcon: {
    width: 24,
    height: 24,
    padding: 4,
    transition: 'transform 0.2s ease',
    '& svg': {
      width: 16,
      height: 16
    }
  },
  iconOpen: {
    transform: 'rotate(180deg)'
  },
  chipContent: {
    display: 'flex',
    alignItems: 'center',
    gap: 4
  },
  rotateIcon: {
    transform: 'rotate(180deg)',
    transition: 'transform 200ms ease'
  },
  chipIcon: {
    width: 16,
    height: 16,
    transition: 'transform 200ms ease'
  },
  dropdownIcon: {
    width: 16,
    height: 16,
    transition: 'transform 200ms ease'
  },
  chipText: {
    display: 'inline-block',
    padding: '2px 6px'
  },
  selectionSummary: {
    background: '#F5F5F5',
    borderRadius: '4px',
    padding: '4px 12px'
  },
  dropButton: {
    borderRadius: '1000px',
    padding: '12px 20px',
    border: `1px solid ${mixins.amethyst}`,
    fontFamily: mixins.fontFamilyPlusSans,
    backgroundColor: mixins.amethyst,
    color: mixins.chalkWhiteFont,
    fontSize: mixins.fontSize14,
    fontWeight: mixins.fontWeight500,
    lineHeight: '24px',
    width: 258,
    height: 40,
    boxSizing: 'border-box',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: mixins.amethyst,
      color: mixins.chalkWhiteFont
    },
    '&.Mui-disabled': {
      backgroundColor: mixins.bulkEditCtaButtonDisabledBGColor,
      color: mixins.bulkEditCtaButtonDisabledColor,
      border: `1px solid ${mixins.bulkEditCtaButtonDisabledBGColor}`,
      cursor: 'not-allowed'
    }
  },
  menuList: {
    padding: 0
  },
  disabledRow: {
    cursor: 'not-allowed',
    pointerEvents: 'none',
    '&:hover': {
      cursor: 'not-allowed',
      backgroundColor: 'transparent'
    }
  }
});

export default TabularStyles;
